import React, { useState, useEffect, useCallback } from 'react';
import './index.css';
import ReactMarkdown from 'react-markdown';
import config from './config';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [sailboatModel, setSailboatModel] = useState('');
  const [researchResult, setResearchResult] = useState(null);
  const [remainingCredits, setRemainingCredits] = useState(null);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [cacheInfo, setCacheInfo] = useState(null);

  const fetchUserCredits = useCallback(async (email) => {
    try {
      const response = await config.apiRequest(`/user/quote/${encodeURIComponent(email)}`, {
        method: 'GET',
      });

      if (response.ok) {
        const data = await response.json();
        setRemainingCredits(data.quote);
      } else {
        // If user doesn't exist, they get 5 credits by default
        setRemainingCredits(5);
      }
    } catch (error) {
      console.error('Error fetching credits:', error);
      setRemainingCredits(5); // Default fallback
    }
  }, []);

  const handleOAuthCallback = useCallback(async (code) => {
    try {
      setLoading(true);
      setError(null);

      // Exchange authorization code for tokens via your backend
      const response = await config.apiRequest('/auth/google', {
        method: 'POST',
        body: JSON.stringify({ code })
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        
        // Fetch the latest credits from the database to ensure we have the most up-to-date information
        await fetchUserCredits(data.user.email);
        
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        throw new Error('Authentication failed');
      }
    } catch (error) {
      console.error('OAuth callback error:', error);
      setError('Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [fetchUserCredits]);

  // Initialize Google OAuth with redirect flow
  useEffect(() => {
    // Check if we're returning from Google OAuth redirect
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    
    if (code && state === 'google_oauth') {
      // Handle the OAuth callback
      handleOAuthCallback(code);
    }
  }, [handleOAuthCallback]);

  const handleGoogleSignIn = () => {
    // Redirect to Google OAuth
    const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=981110436825-sbaecgetipaa5fo80p9t6l32lpjdjsh1.apps.googleusercontent.com&` +
      `redirect_uri=${encodeURIComponent('https://research.keeldragger.com/')}&` +
      `response_type=code&` +
      `scope=email profile openid&` +
      `state=google_oauth`;
    
    window.location.href = googleAuthUrl;
  };

  const handleResearch = async () => {
    if (!sailboatModel.trim()) {
      setError('Please enter a sailboat model');
      return;
    }

    if (remainingCredits <= 0) {
      setError('You have no remaining research credits. Please contact support.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    setResearchResult(null); // Clear previous results
    setCacheInfo(null); // Clear previous cache info

    try {
      console.log('Making API request to research endpoint...');
      
      const response = await config.apiRequest('/research', {
        method: 'POST',
        body: JSON.stringify({
          sailboatModel: sailboatModel.trim(),
          userId: user.email
        }),
        signal: AbortSignal.timeout(900000) // 15 minutes timeout
      });

      if (!response.ok) {
        // Handle specific error responses
        if (response.status === 408) {
          // Request timeout from Lambda
          const timeoutData = await response.json();
          setError(timeoutData.error || 'Research is taking longer than expected. Please try again.');
          return;
        } else if (response.status === 403) {
          const errorData = await response.json();
          setError(errorData.error || 'You have no remaining research credits.');
          return;
        } else if (response.status === 400) {
          const errorData = await response.json();
          setError(errorData.error || 'Invalid request. Please check your input.');
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Handle async job response
      if (response.status === 202 && data.jobId) {
        setCurrentJobId(data.jobId);
        setSuccess('Research started! This may take awhile...');
        // Start polling for results
        startPolling(data.jobId);
      } else if (data.research) {
        // Handle direct research data (cached results)
        setResearchResult(data.research);
        setRemainingCredits(data.remainingQuotes);
        setCacheInfo(data.cacheInfo || { cached: false });
        setSuccess('Research completed successfully!');
      } else if (data.body) {
        // Handle Lambda response format
        const bodyData = JSON.parse(data.body);
        if (bodyData.research) {
          setResearchResult(bodyData.research);
          setRemainingCredits(bodyData.remainingQuotes);
          setCacheInfo(bodyData.cacheInfo || { cached: false });
          setSuccess('Research completed successfully!');
        } else if (bodyData.error) {
          setError(bodyData.error);
        } else {
          setError('Unexpected response format');
        }
      } else if (data.error) {
        setError(data.error);
      } else {
        setError('Unexpected response format');
      }
      
    } catch (error) {
      console.error('Research error:', error);
      if (error.name === 'AbortError') {
        setError('Request timed out. The AI is taking longer than expected. Please try again.');
      } else {
        setError(error.message || 'An error occurred during research');
      }
    } finally {
      setLoading(false);
    }
  };

  const startPolling = useCallback(async (jobId) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await config.apiRequest(`/status?jobId=${jobId}`, {
          method: 'GET',
        });

        if (response.ok) {
          const data = await response.json();
          
          if (data.status === 'completed') {
            clearInterval(pollInterval);
            setResearchResult(data.result);
            setCacheInfo(data.cacheInfo || { cached: false });
            setSuccess('Research completed successfully!');
            setLoading(false);
            setCurrentJobId(null);
            // Update remaining quotes (decrement by 1)
            if (remainingCredits !== null) {
              setRemainingCredits(remainingCredits - 1);
            }
          } else if (data.status === 'failed') {
            clearInterval(pollInterval);
            setError(data.error || 'Research failed');
            setLoading(false);
            setCurrentJobId(null);
          }
          // If status is 'processing', continue polling
        } else {
          console.error('Status check failed:', response.status);
        }
      } catch (error) {
        console.error('Polling error:', error);
        // Continue polling on error, but maybe reduce frequency
      }
    }, 2000); // Poll every 2 seconds

    // Stop polling after 10 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      if (loading) {
        setError('Research is taking longer than expected. Please try again.');
        setLoading(false);
        setCurrentJobId(null);
      }
    }, 600000); // 10 minutes
  }, [loading, remainingCredits]);

  const handleDownloadHTML = () => {
    if (!researchResult) return;
    
    // Convert markdown to HTML using a simple markdown parser
    const convertMarkdownToHTML = (markdown) => {
      // Split into lines for better processing
      const lines = markdown.split('\n');
      let html = '';
      let inOrderedList = false;
      let inUnorderedList = false;
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmedLine = line.trim();
        
        // Headers
        if (trimmedLine.startsWith('### ')) {
          closeLists();
          html += `<h3>${trimmedLine.substring(4)}</h3>\n`;
        } else if (trimmedLine.startsWith('## ')) {
          closeLists();
          html += `<h2>${trimmedLine.substring(3)}</h2>\n`;
        } else if (trimmedLine.startsWith('# ')) {
          closeLists();
          html += `<h1>${trimmedLine.substring(2)}</h1>\n`;
        }
        // Ordered lists (numbered)
        else if (/^\d+\.\s/.test(trimmedLine)) {
          if (!inOrderedList) {
            if (inUnorderedList) {
              html += '</ul>\n';
              inUnorderedList = false;
            }
            html += '<ol>\n';
            inOrderedList = true;
          }
          const content = trimmedLine.replace(/^\d+\.\s/, '');
          html += `<li>${formatInlineMarkdown(content)}</li>\n`;
        }
        // Unordered lists (bullets)
        else if (/^[-*]\s/.test(trimmedLine)) {
          if (!inUnorderedList) {
            if (inOrderedList) {
              html += '</ol>\n';
              inOrderedList = false;
            }
            html += '<ul>\n';
            inUnorderedList = true;
          }
          const content = trimmedLine.replace(/^[-*]\s/, '');
          html += `<li>${formatInlineMarkdown(content)}</li>\n`;
        }
        // Empty line - close lists and add paragraph break
        else if (trimmedLine === '') {
          closeLists();
          html += '\n';
        }
        // Regular paragraph
        else {
          closeLists();
          html += `<p>${formatInlineMarkdown(trimmedLine)}</p>\n`;
        }
      }
      
      // Close any remaining lists
      closeLists();
      
      function closeLists() {
        if (inOrderedList) {
          html += '</ol>\n';
          inOrderedList = false;
        }
        if (inUnorderedList) {
          html += '</ul>\n';
          inUnorderedList = false;
        }
      }
      
      function formatInlineMarkdown(text) {
        return text
          // Bold and italic
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*(.*?)\*/g, '<em>$1</em>')
          // Code blocks
          .replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>')
          .replace(/`(.*?)`/g, '<code>$1</code>')
          // Links
          .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank">$1</a>');
      }
      
      return html;
    };
    
    const formattedContent = convertMarkdownToHTML(researchResult);
    
    const htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Research Results - ${sailboatModel}</title>
    <style>
        body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
            max-width: 800px; 
            margin: 0 auto; 
            padding: 20px; 
            line-height: 1.6; 
            color: #333;
            background-color: #fff;
        }
        h1 { 
            color: #1976d2; 
            border-bottom: 2px solid #1976d2;
            padding-bottom: 10px;
            margin-bottom: 20px;
        }
        h2 { 
            color: #1976d2; 
            margin-top: 30px;
            margin-bottom: 15px;
        }
        h3 { 
            color: #1976d2; 
            margin-top: 25px;
            margin-bottom: 10px;
        }
        p { 
            margin-bottom: 15px; 
        }
        ul, ol { 
            margin: 15px 0; 
            padding-left: 30px; 
        }
        li { 
            margin-bottom: 8px; 
        }
        code { 
            background-color: #f4f4f4; 
            padding: 2px 6px; 
            border-radius: 3px; 
            font-family: 'Courier New', monospace;
            font-size: 0.9em;
        }
        pre { 
            background-color: #f8f8f8; 
            padding: 15px; 
            border-radius: 5px; 
            overflow-x: auto;
            border-left: 4px solid #1976d2;
        }
        pre code { 
            background: none; 
            padding: 0; 
        }
        a { 
            color: #1976d2; 
            text-decoration: none; 
        }
        a:hover { 
            text-decoration: underline; 
        }
        strong { 
            font-weight: bold; 
        }
        em { 
            font-style: italic; 
        }
        .cache-info { 
            background: #f0f8ff; 
            padding: 15px; 
            border-radius: 8px; 
            margin-bottom: 20px; 
            font-size: 14px; 
            border: 1px solid #e3f2fd;
        }
        .timestamp { 
            color: #666; 
            font-size: 12px; 
            margin-bottom: 20px;
        }
        hr {
            border: none;
            border-top: 1px solid #eee;
            margin: 30px 0;
        }
        .content {
            background: #fafafa;
            padding: 20px;
            border-radius: 8px;
            border: 1px solid #e0e0e0;
        }
    </style>
</head>
<body>
    <h1><img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==" alt="KeelDragger" style="height: 24px; width: 24px; vertical-align: middle; margin-right: 8px;">Research Results: ${sailboatModel}</h1>
    <div class="cache-info">
        ${cacheInfo?.cached ? 
          `📋 This research was retrieved from cache (cached on: ${new Date(cacheInfo.cacheDate).toLocaleString()})` : 
          `🆕 This is fresh research completed on: ${new Date().toLocaleString()}`
        }
    </div>
    <div class="timestamp">Downloaded on: ${new Date().toLocaleString()}</div>
    <hr>
    <div class="content">
        ${formattedContent}
    </div>
</body>
</html>`;
    
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sailboat-research-${sailboatModel.replace(/[^a-zA-Z0-9]/g, '-')}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSignOut = () => {
    setUser(null);
    setResearchResult(null);
    setRemainingCredits(null);
    setSailboatModel('');
    setError(null);
    setSuccess(null);
    setCurrentJobId(null);
    setCacheInfo(null);
    
    if (window.google) {
      window.google.accounts.id.disableAutoSelect();
    }
  };

  if (!user) {
    return (
      <div style={styles.container}>
        <div style={styles.loginCard}>
          <h1 style={styles.title}>
            <img src="/keeldragger-logo.png" alt="KeelDragger" style={styles.logoLarge} />
            Sailboat Research
          </h1>
          <p style={styles.subtitle}>Keeldragger AI-Powered Sailboat Analysis</p>
          
          <button onClick={handleGoogleSignIn} style={styles.googleSignInButton}>
            <div style={styles.googleIcon}>G</div>
            Sign in with Google
          </button>
          
          {loading && (
            <div style={styles.loadingMessage}>
              <div style={styles.spinner}></div>
              Authenticating...
            </div>
          )}
          
          {error && (
            <div style={styles.errorMessage}>
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <h1 style={styles.headerTitle}>
            <img src="/keeldragger-logo.png" alt="KeelDragger" style={styles.logo} />
            Sailboat Research
          </h1>
          <div style={styles.userInfo}>
            <img src={user.picture} alt="Profile" style={styles.profilePic} />
            <div>
              <div style={styles.userName}>{user.name}</div>
              <div style={styles.creditInfo}>Credits: {remainingCredits}</div>
            </div>
            <button onClick={handleSignOut} style={styles.signOutButton}>
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main style={styles.main}>
        <div style={styles.contentCard}>
          <h2 style={styles.contentTitle}>Get Keeldragger AI-Powered Sailboat Research</h2>
          <p style={styles.contentSubtitle}>
            Enter any sailboat model for comprehensive analysis
          </p>

          {remainingCredits !== null && (
            <div style={styles.creditAlert}>
              You have {remainingCredits} research credits remaining
            </div>
          )}

          <div style={styles.inputContainer}>
            <input
              type="text"
              placeholder="e.g., Beneteau Oceanis 46.1, Catalina 34, Hunter 42"
              value={sailboatModel}
              onChange={(e) => setSailboatModel(e.target.value)}
              style={styles.textInput}
              disabled={loading}
            />
            <button
              onClick={handleResearch}
              disabled={loading || !sailboatModel.trim()}
              style={styles.researchButton}
            >
              {loading ? 'AI Processing...' : 'Research'}
            </button>
          </div>

          {error && (
            <div style={styles.errorMessage}>
              {error}
            </div>
          )}

          {success && (
            <div style={styles.successMessage}>
              {success}
            </div>
          )}

          {loading && (
            <div style={styles.loadingMessage}>
              <div style={styles.spinner}></div>
              {currentJobId ? (
                <span>Research in progress... Checking status every 2 seconds (Job ID: {currentJobId.substring(0, 8)}...)</span>
              ) : (
                <span>Keeldragger AI is analyzing {sailboatModel}. This may take up to 15 minutes for comprehensive research...</span>
              )}
            </div>
          )}

          {researchResult && (
            <div style={styles.resultsContainer}>
              <div style={styles.resultsHeader}>
                <h3 style={styles.resultsTitle}>
                  <img src="/keeldragger-logo.png" alt="KeelDragger" style={styles.logo} />
                  Research Results for {sailboatModel}
                </h3>
                <button onClick={handleDownloadHTML} style={styles.downloadButton}>
                  📥 Download HTML
                </button>
              </div>
              
              {cacheInfo && (
                <div style={styles.cacheIndicator}>
                  {cacheInfo.cached ? (
                    <span style={styles.cachedIndicator}>
                      📋 Retrieved from cache (cached on: {new Date(cacheInfo.cacheDate).toLocaleString()})
                    </span>
                  ) : (
                    <span style={styles.freshIndicator}>
                      🆕 Fresh research completed on: {new Date().toLocaleString()}
                    </span>
                  )}
                </div>
              )}
              
              <div style={styles.resultsContent}>
                <ReactMarkdown className="results-content">{researchResult}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  loginCard: {
    maxWidth: '400px',
    margin: '100px auto',
    padding: '40px',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    textAlign: 'center'
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#1976d2',
    marginBottom: '10px',
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    justifyContent: 'center'
  },
  subtitle: {
    fontSize: '16px',
    color: '#666',
    marginBottom: '30px',
    lineHeight: '1.5'
  },
  googleSignInButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 24px',
    backgroundColor: 'white',
    border: '1px solid #dadce0',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    color: '#3c4043',
    marginBottom: '20px',
    width: '100%',
    justifyContent: 'center',
    transition: 'all 0.2s ease'
  },
  googleIcon: {
    width: '20px',
    height: '20px',
    backgroundColor: '#4285f4',
    color: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  header: {
    backgroundColor: 'white',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    padding: '20px 0'
  },
  headerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  headerTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1976d2',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  logo: {
    height: '32px',
    width: '32px',
    objectFit: 'contain'
  },
  logoLarge: {
    height: '48px',
    width: '48px',
    objectFit: 'contain'
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px'
  },
  profilePic: {
    width: '40px',
    height: '40px',
    borderRadius: '50%'
  },
  userName: {
    fontWeight: 'bold',
    color: '#333'
  },
  creditInfo: {
    fontSize: '14px',
    color: '#666'
  },
  signOutButton: {
    padding: '8px 16px',
    backgroundColor: '#f44336',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  main: {
    maxWidth: '1200px',
    margin: '40px auto',
    padding: '0 20px'
  },
  contentCard: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
  },
  contentTitle: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '10px',
    textAlign: 'center'
  },
  contentSubtitle: {
    fontSize: '16px',
    color: '#666',
    marginBottom: '30px',
    textAlign: 'center',
    lineHeight: '1.5'
  },
  creditAlert: {
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '20px',
    textAlign: 'center',
    fontWeight: 'bold'
  },
  inputContainer: {
    display: 'flex',
    gap: '15px',
    marginBottom: '20px',
    flexWrap: 'wrap'
  },
  textInput: {
    flex: 1,
    minWidth: '300px',
    padding: '15px',
    border: '2px solid #ddd',
    borderRadius: '8px',
    fontSize: '16px',
    outline: 'none'
  },
  researchButton: {
    padding: '15px 30px',
    backgroundColor: '#1976d2',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    minWidth: '140px'
  },
  loadingMessage: {
    backgroundColor: '#f3e5f5',
    color: '#7b1fa2',
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  spinner: {
    width: '20px',
    height: '20px',
    border: '2px solid #7b1fa2',
    borderTop: '2px solid transparent',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  errorMessage: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '20px'
  },
  successMessage: {
    backgroundColor: '#e8f5e8',
    color: '#2e7d32',
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '20px'
  },
  resultsContainer: {
    marginTop: '30px',
    padding: '20px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e9ecef'
  },
  resultsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px'
  },
  resultsTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#1976d2',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  downloadButton: {
    backgroundColor: '#4CAF50',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    transition: 'background-color 0.2s ease'
  },
  cacheIndicator: {
    marginBottom: '15px',
    padding: '10px',
    borderRadius: '5px',
    fontSize: '14px'
  },
  cachedIndicator: {
    backgroundColor: '#fff3cd',
    color: '#856404',
    padding: '8px 12px',
    borderRadius: '4px',
    border: '1px solid #ffeaa7',
    display: 'inline-block'
  },
  freshIndicator: {
    backgroundColor: '#d4edda',
    color: '#155724',
    padding: '8px 12px',
    borderRadius: '4px',
    border: '1px solid #c3e6cb',
    display: 'inline-block'
  },
  resultsContent: {
    lineHeight: '1.6',
    color: '#333',
    maxHeight: '600px',
    overflowY: 'auto',
    padding: '20px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e9ecef'
  }
};

export default App;